import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Popconfirm,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  createMenuReward,
  getMenuItems,
  getMenuRewards,
  updateMenuReward,
  deleteMenuReward,
} from "../../actions/merchant_actions";

const MenuRewards = () => {
  const accUrn = useSelector((state) => state.account.urn);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [skuOptions, setSkuOptions] = useState([]);
  const [form] = Form.useForm();

  const {
    refetch: refetchRewards,
    isLoading: merchantLoading,
    data,
    isFetching,
  } = useQuery({
    queryKey: ["merchant", accUrn],
    queryFn: () => getMenuRewards({ account_urn: accUrn }),
    enabled: !!accUrn,
  });

  const { isLoading, data: itemsData } = useQuery({
    queryKey: ["menuItems", accUrn],
    queryFn: () => getMenuItems({ account_urn: accUrn }),
    enabled: !!accUrn,
  });

  useEffect(() => {
    if (itemsData) {
      const options = itemsData.map((item) => ({
        label: `${item.data.title} (${item.data.sku})`,
        value: item.data.sku,
      }));
      setSkuOptions(options);
    }
  }, [itemsData]);

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      skus: record.skus,
      number_of_stamps: parseInt(record.number_of_stamps, 10),
    });
    setIsModalOpen(true);
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const payload = {
        skus: values.skus.join(","),
        number_of_stamps: values.number_of_stamps,
      };

      const action = editingRecord
        ? updateMenuReward({
            account_urn: accUrn,
            urn: editingRecord.urn,
            data: payload,
          })
        : createMenuReward({
            account_urn: accUrn,
            data: [payload],
          });

      action
        .then(() => {
          message.success(
            editingRecord ? "Reward updated!" : "Reward created!"
          );
          refetchRewards();
          setIsModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        })
        .catch(() => {
          message.error(
            editingRecord
              ? "Failed to update reward."
              : "Failed to create reward."
          );
        });
    });
  };

  const handleDelete = (record) => {
    deleteMenuReward({
      account_urn: accUrn,
      urn: record.urn,
    })
      .then(() => {
        message.success("Reward deleted!");
        refetchRewards();
      })
      .catch(() => {
        message.error("Failed to delete reward.");
      });
  };

  const columns = [
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      render: (mode) => <Tag color="blue">{mode}</Tag>,
    },
    {
      title: "SKUs",
      dataIndex: "skus",
      key: "skus",
      render: (skus) =>
        skus.map((sku) => (
          <Tag key={sku} color="purple">
            {sku}
          </Tag>
        )),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="green">{type}</Tag>,
    },
    {
      title: "Stamps",
      dataIndex: "number_of_stamps",
      key: "number_of_stamps",
    },
    {
      title: "Discount (%)",
      dataIndex: "allowed_discount_percent",
      key: "allowed_discount_percent",
    },
    {
      title: "Voucher Scheme",
      dataIndex: "voucher_loyalty_scheme_slug",
      key: "voucher_loyalty_scheme_slug",
      render: (slug, record) => (
        <Tooltip title={`URN: ${record.voucher_loyalty_scheme_urn}`}>
          {slug}
        </Tooltip>
      ),
    },
    {
      title: "Stamp Card Scheme",
      dataIndex: "stamp_card_loyalty_scheme_slug",
      key: "stamp_card_loyalty_scheme_slug",
      render: (slug, record) => (
        <Tooltip title={`URN: ${record.stamp_card_loyalty_scheme_urn}`}>
          {slug}
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEditModal(record)}>
            Update
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this reward?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "1rem" }}>
      <Space
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Create Reward
        </Button>
      </Space>

      <Table
        rowKey={(record) => record.urn}
        columns={columns}
        dataSource={data?.data?.body}
        pagination={false}
        bordered
        scroll={{ x: "max-content" }}
        size="middle"
      />

      <Modal
        title={editingRecord ? "Update Reward" : "Create Reward"}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        okText={editingRecord ? "Update" : "Create"}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="SKU"
            name="skus"
            rules={[
              { required: true, message: "Please select at least one SKU" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select products by SKU"
              options={skuOptions}
              loading={isLoading || merchantLoading || isFetching}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Number of Stamps"
            name="number_of_stamps"
            rules={[
              { required: true, message: "Please enter number of stamps" },
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              placeholder="Enter stamp count"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuRewards;
